import React, { useState, useEffect, useRef } from 'react';
import { useCarrito } from '../context/CarritoContext';
import '../components/Ventas.css';
import './Cotizaciones.css';
import jsPDF from 'jspdf';

/**
 * Módulo de Cotizaciones
 * - Permite armar una cotización con productos del inventario
 * - Ajustar precios unitarios (descuento manual) y cantidades
 * - Calcular totales y exportar / imprimir cotización sin afectar stock ni registrar venta
 */
export default function Cotizaciones({ usuario }) {
  const [tab, setTab] = useState('nueva'); // 'nueva' | 'historial'
  const [productos, setProductos] = useState([]);
  const [lineas, setLineas] = useState([]); // {id, concepto, codigo, talla, precioBase, precio, cantidad, descuentoUnit}
  const [busqueda, setBusqueda] = useState('');
  const [nota, setNota] = useState('');
  const [cliente, setCliente] = useState('');
  const [contacto, setContacto] = useState('');
  const [condicionesPie, setCondicionesPie] = useState('');
  const [firma, setFirma] = useState('');
  const [vigenciaDias, setVigenciaDias] = useState(7);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [mensajeGuardado, setMensajeGuardado] = useState(null); // {folio, id_cotizacion}
  const [errorGuardado, setErrorGuardado] = useState('');
  const [descuentoGlobalPct, setDescuentoGlobalPct] = useState(0); // descuento % aplicado sobre precioBase
  const [folio, setFolio] = useState(()=>{
    // Folio persistente en localStorage (separado de ventas reales)
    const raw = localStorage.getItem('folioCotizacion');
    return raw? parseInt(raw) || 1 : 1;
  });
  const [usarIVA, setUsarIVA] = useState(()=>{
    const raw = localStorage.getItem('cot_usa_iva');
    return raw ? raw === '1' : false;
  });
  const [tasaIVA, setTasaIVA] = useState(()=>{
    const raw = localStorage.getItem('cot_tasa_iva');
    return raw ? parseFloat(raw) || 16 : 16; // por defecto 16%
  });
  // Escaneo
  const [scanBuffer, setScanBuffer] = useState('');
  const [lastKeyTime, setLastKeyTime] = useState(0);
  const scanThreshold = 45; // ms entre teclas para considerarlo escaneo
  const inputRef = useRef(null);
  const [flash, setFlash] = useState(false);
  const [variantModal, setVariantModal] = useState({ open:false, options:[], scannedCode:'' });
  const [shareModal, setShareModal] = useState({ open:false, loading:false, wa:'', mail:'', url:'', folio:'', cliente:'', error:'', idCot:null, pdfBlob:null });

  // Función para generar PDF de cotización
  const generarPDFCotizacion = async (id) => {
    try {
      const resp = await fetchWithUserHeader(`/api/cotizaciones/${id}`);
      if(!resp.ok) throw new Error('Error al obtener detalle');
      const data = await resp.json();
      if(!data?.cotizacion) throw new Error('Sin datos');

      const { cotizacion, detalle: lineas } = data;
      const fechaStr = new Date(cotizacion.fecha).toLocaleDateString('es-MX');
      const usarIVAPdf = !!cotizacion.usar_iva;
      const tasaIVAPdf = Number(cotizacion.tasa_iva)||16;
      const subtotalBruto = Number(cotizacion.subtotal_bruto)||0;
      const descuentoTotal = Number(cotizacion.descuento_total)||0;
      const subtotal = Number(cotizacion.subtotal_neto)||0;
      const ivaVal = Number(cotizacion.iva)||0;
      const totalVal = Number(cotizacion.total)||0;

      const doc = new jsPDF();

      // Cargar logo como base64

      // Cargar logo desde /img/logo.png (siempre accesible en producción)
      const getLogoBase64 = () => new Promise((resolve) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.src = '/img/logo.png';
        img.onload = function() {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = function() {
          resolve(null);
        };
      });

      let yPos = 20;
      const logoBase64 = await getLogoBase64();
      if (logoBase64) {
        // Centrado arriba, tamaño 40x40mm
        doc.addImage(logoBase64, 'PNG', 85, yPos, 40, 40);
        yPos += 45;
      } else {
        // Si no hay logo, solo el nombre
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('SAFETYPOINT', 105, yPos + 15, { align: 'center' });
        yPos += 30;
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Cotización', 105, yPos, { align: 'center' });
      yPos += 10;

      // Info de la cotización
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Folio: ${cotizacion.folio}`, 15, yPos);
      doc.text(`Fecha: ${fechaStr}`, 15, yPos + 5);
      if(cotizacion.cliente) doc.text(`Cliente: ${cotizacion.cliente}`, 15, yPos + 10);
      if(cotizacion.contacto) doc.text(`Contacto: ${cotizacion.contacto}`, 15, yPos + 15);
      if(usarIVAPdf) doc.text(`IVA: ${tasaIVAPdf}%`, 15, yPos + 20);

      // Tabla de productos
      yPos = yPos + 30;

      // Cabecera de tabla
      doc.setFillColor(240, 240, 240);
      doc.rect(15, yPos, 180, 7, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Producto', 17, yPos + 5);
      doc.text('Cant', 120, yPos + 5);
      doc.text('Precio', 140, yPos + 5);
      doc.text('Desc.U', 160, yPos + 5);
      doc.text('Importe', 180, yPos + 5);

      // Líneas de productos
      doc.setFont('helvetica', 'normal');
      yPos += 12;

      lineas.forEach((l, idx) => {
        if(yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        
        const descU = Number(l.descuento_unitario)||0;
        const concepto = (l.concepto||'').substring(0, 40); // Limitar longitud
        
        doc.text(concepto, 17, yPos);
        doc.text(String(l.cantidad), 120, yPos);
        doc.text(fmtMon(l.precio_unitario), 140, yPos);
        doc.text(descU > 0 ? fmtMon(descU) : '-', 160, yPos);
        doc.text(fmtMon(l.importe), 180, yPos);
        
        yPos += 7;
      });
      
      // Totales
      yPos += 5;
      doc.setFont('helvetica', 'bold');
      doc.text('Subtotal bruto:', 130, yPos);
      doc.text(fmtMon(subtotalBruto), 180, yPos);
      
      yPos += 7;
      doc.text('Descuento:', 130, yPos);
      doc.text(fmtMon(descuentoTotal), 180, yPos);
      
      yPos += 7;
      doc.text('Subtotal neto:', 130, yPos);
      doc.text(fmtMon(subtotal), 180, yPos);
      
      if(usarIVAPdf) {
        yPos += 7;
        doc.text(`IVA (${tasaIVAPdf}%):`, 130, yPos);
        doc.text(fmtMon(ivaVal), 180, yPos);
      }
      
      yPos += 7;
      doc.setFontSize(11);
      doc.text('Total:', 130, yPos);
      doc.text(fmtMon(totalVal), 180, yPos);
      
      // Condiciones y firma
      if(cotizacion.condiciones_pie) {
        yPos += 10;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const condiciones = doc.splitTextToSize(cotizacion.condiciones_pie, 180);
        doc.text(condiciones, 15, yPos);
        yPos += condiciones.length * 5;
      }
      
      if(cotizacion.firma) {
        yPos += 10;
        doc.setFontSize(9);
        doc.text(`Firma: ${cotizacion.firma}`, 15, yPos);
      }
      
      // Nota al pie
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text('Esta cotización no constituye una factura.', 105, 285, { align: 'center' });
      
      return doc.output('blob');
    } catch(e) {
      throw new Error('Error generando PDF: ' + e.message);
    }
  };

  // ---- Historial de Cotizaciones ----
  const [histLista, setHistLista] = useState([]);
  const [histCargando, setHistCargando] = useState(false);
  const [histError, setHistError] = useState(null);
  const [histDetalle, setHistDetalle] = useState(null); // {cotizacion, detalle:[]}
  const [reimprimiendo, setReimprimiendo] = useState(false);

  const fmtMon = (n) => new Intl.NumberFormat('es-MX',{style:'currency',currency:'MXN'}).format(Number(n)||0);

  // Helper fetch que añade cabecera X-Usuario con info mínima (si está autenticado)
  const fetchWithUserHeader = (url, opts = {}) => {
    const headers = Object.assign({}, opts.headers || {});
    if (usuario) {
      try { headers['X-Usuario'] = JSON.stringify({ id: usuario.email || usuario.nombre, nombre: usuario.nombre, rol: usuario.rol }); } catch(e) { headers['X-Usuario'] = String(usuario.email || usuario.nombre); }
    }
    return fetch(url, Object.assign({}, opts, { headers }));
  };

  const cargarHistorial = async () => {
    setHistCargando(true); setHistError(null);
    try {
  const r = await fetchWithUserHeader('/api/cotizaciones');
      if(!r.ok) throw new Error('Error al listar');
      const data = await r.json();
      setHistLista(data);
    } catch(e){ setHistError(e.message); }
    finally { setHistCargando(false); }
  };

  const verHistorial = async (id) => {
    try {
  const r = await fetchWithUserHeader(`/api/cotizaciones/${id}`);
      if(!r.ok) throw new Error('Error al obtener detalle');
      const data = await r.json();
      setHistDetalle(data);
    } catch(e){ alert(e.message); }
  };

  const eliminarHistorial = async (id, folio) => {
    if(!window.confirm(`¿Eliminar cotización ${folio || id}? Esta acción no se puede deshacer.`)) return;
    try {
  const r = await fetchWithUserHeader(`/api/cotizaciones/${id}`, { method:'DELETE' });
      if(!r.ok){
        const err = await r.json().catch(()=>({}));
        const msg = err.detalle ? `${err.error}: ${err.detalle}` : (err.error || 'Error al eliminar');
        throw new Error(msg);
      }
      if(histDetalle && histDetalle.cotizacion?.id_cotizacion === id){ setHistDetalle(null); }
      setHistLista(prev => prev.filter(c => c.id_cotizacion !== id));
    } catch(e){ alert(e.message); }
  };

  const imprimirHistorial = async (id) => {
    setReimprimiendo(true);
    try {
      let data = histDetalle;
      if(!data || data?.cotizacion?.id_cotizacion !== id){
  const r = await fetchWithUserHeader(`/api/cotizaciones/${id}`);
        if(!r.ok) throw new Error('Error al obtener detalle');
        data = await r.json();
      }
      if(!data?.cotizacion) throw new Error('Sin datos');
      const { cotizacion, detalle: lineas } = data;
  const fechaStr = new Date(cotizacion.fecha).toLocaleDateString('es-MX');
      const usarIVAImp = !!cotizacion.usar_iva;
      const tasaIVAImp = Number(cotizacion.tasa_iva)||16;
      const subtotalBruto = Number(cotizacion.subtotal_bruto)||0;
      const descuentoTotal = Number(cotizacion.descuento_total)||0;
      const subtotal = Number(cotizacion.subtotal_neto)||0;
      const ivaVal = Number(cotizacion.iva)||0;
      const totalVal = Number(cotizacion.total)||0;
      const win = window.open('', 'COT_PRINT', 'height=700,width=750');
      if(!win){ alert('Popup bloqueado'); setReimprimiendo(false); return; }
      win.document.write(`<!DOCTYPE html><html><head><title>${cotizacion.folio}</title><style>
        body{ font-family: Arial, sans-serif; margin:20px; }
        h1{ font-size:20px; margin:0 0 4px; }
        .meta{ font-size:12px; color:#444; margin-bottom:14px; }
        table{ width:100%; border-collapse: collapse; font-size:12px; }
        th,td{ border:1px solid #999; padding:4px; }
        th{ background:#eee; }
        tfoot td{ font-weight:bold; }
        .header-logo{ display:flex; align-items:center; gap:14px; margin-bottom:10px; }
        .header-logo img{ height:56px; object-fit:contain; display:block; }
        .brand-title{ font-size:18px; font-weight:700; margin:0; }
      </style></head><body>
        <div class='header-logo'>
          <img src='/logo.png' alt='SafetyPoint' onerror="this.style.display='none';document.getElementById('logo-fallback').style.display='block';" />
          <div>
            <h1 class='brand-title' style='margin:0 0 4px'>Cotización</h1>
            <div style='font-size:11px;color:#555;'>Reimpresión</div>
          </div>
        </div>
        <div id='logo-fallback' style='display:none;font-size:14px;font-weight:600;margin-bottom:6px;'>SAFETYPOINT</div>
  <div class='meta'>Folio: ${cotizacion.folio} | Fecha: ${fechaStr}${cotizacion.cliente? ' | Cliente: '+(cotizacion.cliente): ''}${cotizacion.contacto? ' | Contacto: '+(cotizacion.contacto): ''}${usarIVAImp? ' | IVA '+tasaIVAImp+'%':''}</div>
        <table><thead><tr><th>Producto</th><th>Cant</th><th>Precio</th><th>Desc.U</th><th>Importe</th></tr></thead><tbody>
          ${lineas.map(l => {
            const descU = Number(l.descuento_unitario)||0;
            return `<tr><td>${l.concepto||''}</td><td>${l.cantidad}</td><td>${fmtMon(l.precio_unitario)}</td><td>${descU>0?fmtMon(descU):'-'}</td><td>${fmtMon(l.importe)}</td></tr>`;
          }).join('')}
        </tbody><tfoot>
          <tr><td colspan='4' style='text-align:right'>Subtotal bruto:</td><td>${fmtMon(subtotalBruto)}</td></tr>
          <tr><td colspan='4' style='text-align:right'>Descuento:</td><td>${fmtMon(descuentoTotal)}</td></tr>
          <tr><td colspan='4' style='text-align:right'>Subtotal neto:</td><td>${fmtMon(subtotal)}</td></tr>
          ${usarIVAImp? `<tr><td colspan='4' style='text-align:right'>IVA (${tasaIVAImp}%):</td><td>${fmtMon(ivaVal)}</td></tr>`:''}
          <tr><td colspan='4' style='text-align:right'>Total:</td><td>${fmtMon(totalVal)}</td></tr>
        </tfoot></table>
        ${cotizacion.condiciones_pie? `<div class="nota" style='font-size:12px;margin-top:10px'>${(cotizacion.condiciones_pie||'').replace(/</g,'&lt;')}</div>`:''}
        <div style='margin-top:18px;'>
          <div style='height:1px;background:#999;margin:24px 0 6px'></div>
          <div style='font-size:11px;color:#444'>Firma${cotizacion.firma? ': '+(cotizacion.firma):''}</div>
        </div>
        <script>(function(){function after(){try{window.print();}catch(e){} setTimeout(()=>{try{window.close();}catch(e){}},600);} const imgs=[...document.images]; if(imgs.length===0){after();return;} let ld=0; const done=()=>{if(++ld===imgs.length) after();}; imgs.forEach(im=>{ if(im.complete) done(); else { im.addEventListener('load',done); im.addEventListener('error',done);} }); })();</script>
      </body></html>`);
      win.document.close(); win.focus();
    } catch(e){ alert('No se pudo imprimir: '+ e.message); }
    finally { setReimprimiendo(false); }
  };

  const imprimirGuardada = async (id) => {
    if(!id) return;
    // reutilizar imprimirHistorial (extraer lógica mínima)
    try{ await imprimirHistorial(id); }catch(e){ alert('No se pudo imprimir: '+ (e.message||e)); }
  };

  // ---- Filtros Historial (componente interno) ----
  const HistorialFiltros = () => {
    const [fFol, setFFol] = useState('');
    const [fDesde, setFDesde] = useState('');
    const [fHasta, setFHasta] = useState('');
    const aplicar = async () => {
      setHistCargando(true); setHistError(null); setHistDetalle(null);
      try {
        const params = new URLSearchParams();
        if(fFol) params.append('folio', fFol);
        if(fDesde) params.append('desde', fDesde);
        if(fHasta) params.append('hasta', fHasta);
  const r = await fetchWithUserHeader('/api/cotizaciones?'+params.toString());
        if(!r.ok) throw new Error('Error al filtrar');
        const data = await r.json();
        setHistLista(data);
      } catch(e){ setHistError(e.message); }
      finally { setHistCargando(false); }
    };
    const limpiarFiltros = () => { setFFol(''); setFDesde(''); setFHasta(''); cargarHistorial(); };
    return (
      <div style={{marginBottom:12,display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end'}}>
        <div style={{display:'flex',flexDirection:'column'}}>
          <label style={{fontSize:'10px',opacity:.7}}>Folio</label>
          <input value={fFol} onChange={e=>setFFol(e.target.value)} placeholder="COT-..." style={{width:110}} />
        </div>
        <div style={{display:'flex',flexDirection:'column'}}>
          <label style={{fontSize:'10px',opacity:.7}}>Desde</label>
          <input type="date" value={fDesde} onChange={e=>setFDesde(e.target.value)} />
        </div>
        <div style={{display:'flex',flexDirection:'column'}}>
          <label style={{fontSize:'10px',opacity:.7}}>Hasta</label>
          <input type="date" value={fHasta} onChange={e=>setFHasta(e.target.value)} />
        </div>
        <button onClick={aplicar} disabled={histCargando} className="btn-confirmar" style={{height:32}}>{histCargando? '...' : 'Filtrar'}</button>
        <button onClick={limpiarFiltros} type="button" className="btn-cancelar" style={{height:32}}>Limpiar</button>
        <button onClick={cargarHistorial} disabled={histCargando} style={{height:32}} className="btn-confirmar">↻</button>
      </div>
    );
  };

  // Utilidad normalizar (igual que Carrito)
  const normalizarSerie = (s) => {
    if (s === undefined || s === null) return '';
    try { return s.toString().toLowerCase().replace(/\s+/g,'').replace(/[-_]/g,''); } catch { return ''; }
  };

  useEffect(()=>{
    fetch('/api/productos')
      .then(r=> r.ok? r.json(): [])
      .then(data => {
        const norm = Array.isArray(data)? data.map(p => ({
          id: p.id_producto,
          concepto: p.concepto,
          codigo: p.codigo,
          talla: p.talla,
          precio: Number(p.precio) || 0,
          cantidad: p.cantidad
        })) : [];
        setProductos(norm);
      }).catch(()=>{});
  },[]);

  useEffect(()=>{ if(inputRef.current) inputRef.current.focus(); },[]);

  // Handler global de teclas para buffer de escaneo
  useEffect(()=>{
    const handler = (e) => {
      // Evitar interferir cuando el foco está en inputs/textarea de edición (excepto el input oculto del escáner)
      const ae = document.activeElement;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA') && ae !== inputRef.current) return;
      const now = Date.now();
      if (e.key === 'Enter') {
        const code = scanBuffer.trim();
        if(code.length>0){
          const normCode = normalizarSerie(code);
          const matches = productos.filter(p => normalizarSerie(p.codigo) === normCode);
          if(matches.length === 1){
            agregarProducto(matches[0]);
          } else if(matches.length > 1){
            setVariantModal({ open:true, options: matches, scannedCode: code });
          } else {
            // fallback a endpoint serie (igual que Carrito)
            fetch(`/api/productos/serie/${encodeURIComponent(code)}`)
              .then(r=> r.ok? r.json(): null)
              .then(data => { if(data && data.id_producto){ agregarProducto({
                id: data.id_producto,
                concepto: data.concepto,
                codigo: data.codigo,
                talla: data.talla,
                precio: Number(data.precio)||0,
                cantidad: data.cantidad
              }); }}).catch(()=>{});
          }
          setScanBuffer('');
          setFlash(true); setTimeout(()=>setFlash(false),200);
          setTimeout(()=> inputRef.current && (inputRef.current.value=''), 60);
        }
        return;
      }
      if(e.key.length === 1){
        if(now - lastKeyTime > scanThreshold){ setScanBuffer(e.key); } else { setScanBuffer(prev => prev + e.key); }
        setLastKeyTime(now);
      }
    };
    window.addEventListener('keydown', handler);
    return ()=> window.removeEventListener('keydown', handler);
  },[scanBuffer,lastKeyTime,productos]);

  const agregarProducto = (p) => {
    setLineas(prev => {
      const ya = prev.find(l => l.id === p.id);
      if(ya){
        return prev.map(l => l.id === p.id ? { ...l, cantidad: l.cantidad + 1 } : l);
      }
      return [...prev, {
        id: p.id,
        concepto: p.concepto,
        codigo: p.codigo,
        talla: p.talla,
        precioBase: Number(p.precio) || 0,
        precio: Number(p.precio) || 0,
        cantidad: 1,
        descuentoUnit: 0
      }];
    });
  };

  const seleccionarVariante = (prod) => {
    agregarProducto(prod);
    setVariantModal({ open:false, options:[], scannedCode:'' });
    setTimeout(()=> inputRef.current?.focus(), 50);
  };
  const cerrarVariante = () => { setVariantModal({ open:false, options:[], scannedCode:'' }); setTimeout(()=> inputRef.current?.focus(), 50); };

  const actualizarLinea = (id, campo, valor) => {
    setLineas(prev => prev.map(l => l.id === id ? { ...l, [campo]: valor } : l));
  };
  const eliminarLinea = (id) => setLineas(prev => prev.filter(l => l.id !== id));
  const limpiar = () => { setLineas([]); setNota(''); setCliente(''); setContacto(''); setCondicionesPie(''); setFirma(''); };

  const filtrados = productos.filter(p => {
    if(!busqueda) return true;
    const b = busqueda.toLowerCase();
    return (p.concepto||'').toLowerCase().includes(b) || (p.codigo||'').toLowerCase().includes(b) || (p.talla||'').toLowerCase().includes(b);
  });

  const subtotal = lineas.reduce((a,l)=> a + (l.precio * l.cantidad),0);
  const descuentoTotal = lineas.reduce((a,l)=> a + ( (l.precioBase - l.precio) * l.cantidad ),0);
  const subtotalBruto = subtotal + descuentoTotal;
  const iva = usarIVA ? subtotal * (tasaIVA/100) : 0;
  const total = subtotal + iva;

  const aplicarDescuentoGlobal = (pct) => {
    if(pct < 0) pct = 0; if(pct>100) pct = 100;
    setDescuentoGlobalPct(pct);
    setLineas(prev => prev.map(l => ({
      ...l,
      // recalcula precio a partir de precioBase; mantiene precioBase intacto
      precio: Number((l.precioBase * (1 - pct/100)).toFixed(2))
    })));
  };

  const imprimir = () => {
    setImprimiendo(true);
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-MX');
    const vence = new Date(fecha.getTime() + (vigenciaDias*24*60*60*1000)).toLocaleDateString('es-MX');
    const folioActual = folio; // snapshot
    // incrementar folio para próxima
    const siguiente = folioActual + 1;
    setFolio(siguiente);
    localStorage.setItem('folioCotizacion', String(siguiente));
    const win = window.open('', 'COTIZACION', 'height=700,width=750');
    if(!win){ setImprimiendo(false); return alert('Popup bloqueado'); }
    const fmt = n => new Intl.NumberFormat('es-MX',{style:'currency',currency:'MXN'}).format(n);
    win.document.write(`<!DOCTYPE html><html><head><title>Cotización</title><style>
      body{ font-family: Arial, sans-serif; margin:20px; }
      h1{ font-size:20px; margin:0 0 4px; }
      .meta{ font-size:12px; color:#444; margin-bottom:14px; }
      table{ width:100%; border-collapse: collapse; font-size:12px; }
      th,td{ border:1px solid #999; padding:4px; }
      th{ background:#eee; }
      tfoot td{ font-weight:bold; }
      .nota{ margin-top:18px; font-size:12px; white-space:pre-wrap; }
      .header-logo{ display:flex; align-items:center; gap:14px; margin-bottom:10px; }
      .header-logo img{ height:56px; object-fit:contain; display:block; }
      .brand-title{ font-size:18px; font-weight:700; margin:0; }
    </style></head><body>
      <div class="header-logo">
        <img src="/logo.png" alt="SafetyPoint" onerror="this.style.display='none';document.getElementById('logo-fallback').style.display='block';" />
        <div>
          <h1 class="brand-title" style="margin:0 0 4px">Cotización</h1>
          <div style="font-size:11px;color:#555;">Generada por SAFETYPOINT</div>
        </div>
      </div>
      <div id="logo-fallback" style="display:none;font-size:14px;font-weight:600;margin-bottom:6px;">SAFETYPOINT</div>
  <div class="meta">Folio: COT-${folioActual.toString().padStart(5,'0')} | Fecha: ${fechaStr} | Vigente hasta: ${vence} | Cliente: ${cliente || 'N/D'}${contacto? ' | Contacto: '+(contacto.replace(/</g,'&lt;')):''}${usarIVA? ' | IVA '+tasaIVA+'%':''}</div>
      <table><thead><tr><th>Producto</th><th>Código</th><th>Talla</th><th>Cant</th><th>Precio</th><th>Desc.U</th><th>Importe</th></tr></thead><tbody>
        ${lineas.map(l => {
          const descU = (l.precioBase - l.precio);
          const importe = l.precio * l.cantidad;
          return `<tr><td>${l.concepto || ''}</td><td>${l.codigo||''}</td><td>${l.talla||''}</td><td>${l.cantidad}</td><td>${fmt(l.precio)}</td><td>${descU>0?fmt(descU):'-'}</td><td>${fmt(importe)}</td></tr>`;
        }).join('')}
      </tbody><tfoot>
        <tr><td colspan="6" style="text-align:right">Subtotal bruto:</td><td>${fmt(subtotalBruto)}</td></tr>
        <tr><td colspan="6" style="text-align:right">Descuento:</td><td>${fmt(descuentoTotal)}</td></tr>
        <tr><td colspan="6" style="text-align:right">Subtotal neto:</td><td>${fmt(subtotal)}</td></tr>
        ${usarIVA? `<tr><td colspan="6" style="text-align:right">IVA (${tasaIVA}%):</td><td>${fmt(iva)}</td></tr>`:''}
        <tr><td colspan="6" style="text-align:right">Total:</td><td>${fmt(total)}</td></tr>
      </tfoot></table>
      ${nota? `<div class="nota"><strong>Notas:</strong> ${nota.replace(/</g,'&lt;')}</div>`:''}
      ${condicionesPie? `<div class="nota"><strong>Condiciones:</strong> ${condicionesPie.replace(/</g,'&lt;')}</div>`:''}
      <div style="margin-top:26px;display:flex;gap:20px;align-items:flex-end">
        <div style="flex:1 1 50%">
          <div style="height:1px;background:#999;margin:24px 0 6px"></div>
          <div style="font-size:11px;color:#444">Firma${firma? ': '+(firma.replace(/</g,'&lt;')):''}</div>
        </div>
      </div>
      <p style="margin-top:18px;font-size:11px;color:#666">Esta cotización no constituye una factura. Precios sujetos a cambio sin previo aviso hasta confirmar existencia.</p>
      <script>
        (function(){
          function afterImages(){
            try { window.print(); } catch(e) {}
            setTimeout(()=>{ try { window.close(); } catch(e) {} }, 700);
          }
          const imgs = Array.from(document.images);
            if(imgs.length===0){ afterImages(); return; }
            let loaded=0; const done=()=>{ if(++loaded===imgs.length) afterImages(); };
            imgs.forEach(im=>{ if(im.complete) done(); else { im.addEventListener('load',done); im.addEventListener('error',done);} });
        })();
      </script>
    </body></html>`);
    win.document.close(); win.focus();
    setTimeout(()=> setImprimiendo(false), 800);
  };

  // Guardar cotización en backend (sin imprimir)
  const guardar = async () => {
    if(guardando) return;
    setErrorGuardado('');
    if(lineas.length === 0){ setErrorGuardado('No hay líneas en la cotización. Agrega productos antes de guardar.'); return; }
    // validar líneas
    for(const l of lineas){
      if(!l.id){ setErrorGuardado('Una línea no tiene id de producto.'); return; }
      if(Number(l.cantidad) <= 0){ setErrorGuardado(`Cantidad inválida para ${l.concepto || l.id}`); return; }
      if(Number(l.precio) < 0){ setErrorGuardado(`Precio inválido para ${l.concepto || l.id}`); return; }
    }
    setGuardando(true);
    setMensajeGuardado(null);
    // Calcular totales coherentes con backend
    const subtotal_neto = lineas.reduce((a,l)=> a + (Number(l.precio)||0) * (Number(l.cantidad)||0), 0);
    const descuento_total = lineas.reduce((a,l)=> a + ((Number(l.precioBase)||0 - (Number(l.precio)||0)) * (Number(l.cantidad)||0)), 0);
    const subtotal_bruto = subtotal_neto + descuento_total;
    const ivaCalc = usarIVA ? +(subtotal_neto * (tasaIVA/100)).toFixed(2) : 0;
    const totalCalc = +(subtotal_neto + ivaCalc).toFixed(2);
    const payload = {
      items: lineas.map(l => ({
        id_producto: l.id,
        cantidad: Number(l.cantidad)||0,
        precio_unitario: Number(l.precio)||0,
        descuento_unitario: +(Number(l.precioBase||0) - Number(l.precio||0)).toFixed(2)
      })),
      subtotal_bruto: +subtotal_bruto.toFixed(2),
      descuento_total: +descuento_total.toFixed(2),
      subtotal_neto: +subtotal_neto.toFixed(2),
      iva: ivaCalc,
      total: totalCalc,
      descuento_global_pct: Number(descuentoGlobalPct)||0,
      usar_iva: usarIVA,
      tasa_iva: Number(tasaIVA)||0,
      cliente: cliente || null,
      contacto: contacto || null,
      condiciones_pie: condicionesPie || null,
      firma: firma || null,
      vigencia_dias: vigenciaDias || null
    };
    try {
      const resp = await fetchWithUserHeader('/api/cotizaciones', {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify(payload)
      });
      if(!resp.ok){
        const err = await resp.json().catch(()=>({}));
        throw new Error(err.error || 'Error al guardar cotización');
      }
      const data = await resp.json();
      setMensajeGuardado({ folio: data?.cotizacion?.folio, id_cotizacion: data?.cotizacion?.id_cotizacion });
      // Refresh historial in background so the new cotización appears without a full page reload
      try { cargarHistorial(); } catch(e) { /* ignore refresh errors */ }
    } catch (e) {
      setErrorGuardado('No se pudo guardar: ' + e.message);
    } finally {
      setGuardando(false);
    }
  };

  const renderNueva = () => (
    <>
      <h3 style={{margin:'14px 0 8px'}}>Nueva Cotización</h3>
      {/* Input oculto para mantener foco del escáner */}
      <input
        ref={inputRef}
        type="text"
        placeholder="Escanear"
        onBlur={() => setTimeout(()=>{ // si se pierde el foco y no se está en un input/textarea visible, retomarlo
          const ae = document.activeElement;
          if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.tagName === 'SELECT') && ae !== inputRef.current) return; // algún control visible
          inputRef.current?.focus();
        }, 80)}
        style={{position:'absolute',opacity:0,height:0,width:0,pointerEvents:'none'}}
        className={flash? 'scan-flash' : ''}
      />
      <p style={{fontSize:'0.65rem',margin:'4px 0 10px',opacity:0.6}}>Puedes escanear códigos directamente para añadir productos.</p>
      <div style={{display:'flex',gap:'12px',flexWrap:'wrap',marginBottom:'12px'}}>
        <input placeholder="Cliente" value={cliente} onChange={e=>setCliente(e.target.value)} />
        <input placeholder="Contacto" value={contacto} onChange={e=>setContacto(e.target.value)} />
        <input placeholder="Buscar producto" value={busqueda} onChange={e=>setBusqueda(e.target.value)} />
        <input type="number" min={1} value={vigenciaDias} onChange={e=>setVigenciaDias(parseInt(e.target.value)||1)} style={{width:'110px'}} title="Vigencia días" />
        <button onClick={limpiar} className="btn-cancelar">Limpiar</button>
      </div>

      <div className="cotz-grid">
        <div className="cotz-card" style={{flex:'1 1 340px', minWidth:'320px'}}>
          <h4>Catálogo</h4>
          <div className="listado-scroll">
            {filtrados.map(p => (
              <div key={p.id} className="row">
                <div style={{fontSize:'12px'}}>
                  <strong>{p.concepto}</strong> {p.talla && <span className="muted">( {p.talla} )</span>}<br/>
                  <span className="muted" style={{fontSize:'11px'}}>{p.codigo}</span>
                </div>
                <button onClick={()=>agregarProducto(p)} className="btn-modern outline" style={{padding:'6px 10px'}}>Agregar</button>
              </div>
            ))}
            {filtrados.length===0 && <div style={{fontSize:'12px',opacity:.6}}>Sin resultados</div>}
          </div>
        </div>
        <div className="cotz-card" style={{flex:'2 1 480px', minWidth:'380px'}}>
          <h4>Líneas de Cotización</h4>
            {lineas.length>0 && (
              <div style={{display:'flex',flexWrap:'wrap',gap:'12px',marginBottom:10,alignItems:'center'}}>
                <div style={{display:'flex',alignItems:'center',gap:6,fontSize:'11px'}}>
                  <label className="chip iva" style={{cursor:'pointer'}}>
                    <input type="checkbox" checked={usarIVA} onChange={e=>{ setUsarIVA(e.target.checked); localStorage.setItem('cot_usa_iva', e.target.checked? '1':'0'); }} /> IVA
                  </label>
                  {usarIVA && (
                    <input type="number" min={0} max={30} value={tasaIVA} onChange={e=>{ const v=parseFloat(e.target.value)||0; setTasaIVA(v); localStorage.setItem('cot_tasa_iva', String(v)); }} style={{width:60,fontSize:'11px'}} />
                  )}
                </div>
                <div className="chip">Folio: COT-{folio.toString().padStart(5,'0')}</div>
              </div>
            )}
          {lineas.length>0 && (
            <div style={{margin:'0 0 10px',display:'flex',flexDirection:'column',gap:6}}>
              <div style={{fontSize:'12px',fontWeight:600}}>Descuento Global:</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'6px'}}>
                {[0,5,10,15,20,25,30,40,50].map(p => (
                  <button key={p} type="button" onClick={()=>aplicarDescuentoGlobal(p)}
                    className={`btn-modern ${p===descuentoGlobalPct? 'primary':'outline'}`}
                    style={{fontSize:'11px'}}>{p}%</button>
                ))}
                <div style={{display:'flex',alignItems:'center',gap:4}}>
                  <input type="number" min={0} max={100} value={descuentoGlobalPct}
                    onChange={e=>aplicarDescuentoGlobal(parseFloat(e.target.value)||0)}
                    style={{width:60,fontSize:'11px'}} />
                  <span style={{fontSize:'11px'}}>%</span>
                </div>
              </div>
              <small style={{fontSize:'10px',opacity:0.65}}>Se aplica sobre el Precio Base de cada línea. Puedes ajustar manualmente precios individuales después.</small>
            </div>
          )}
          {lineas.length===0 && <p style={{fontSize:'12px',opacity:.7}}>Agrega productos desde el catálogo.</p>}
          {lineas.length>0 && (
            <table className="cotz-table" style={{fontSize:'12px'}}>
              <thead>
                <tr>
                  <th>Producto</th><th>Talla</th><th>Cant</th><th>Precio Base</th><th>Precio</th><th>Desc/U</th><th>Importe</th><th></th>
                </tr>
              </thead>
              <tbody>
                {lineas.map(l => {
                  const descU = (l.precioBase - l.precio);
                  const importe = l.precio * l.cantidad;
                  const precioBaseFmt = (typeof l.precioBase === 'number' && !isNaN(l.precioBase)) ? l.precioBase.toFixed(2) : '0.00';
                  return (
                    <tr key={l.id}>
                      <td>{l.concepto}</td>
                      <td>{l.talla || '-'}</td>
                      <td><input type="number" min={1} value={l.cantidad} onChange={e=> actualizarLinea(l.id,'cantidad', parseInt(e.target.value)||1)} style={{width:'60px'}} /></td>
                      <td>{precioBaseFmt}</td>
                      <td><input type="number" step="0.01" value={l.precio} onChange={e=> actualizarLinea(l.id,'precio', parseFloat(e.target.value)||0)} style={{width:'80px'}} /></td>
                      <td>{descU > 0 ? descU.toFixed(2) : '-'}</td>
                      <td>{importe.toFixed(2)}</td>
                      <td><button onClick={()=>eliminarLinea(l.id)} className="btn-eliminar-linea">✕</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {lineas.length>0 && (
            <>
              {errorGuardado && (
                <div style={{marginBottom:8,padding:8,background:'#ffebee',color:'#b71c1c',border:'1px solid #ffcdd2',borderRadius:4,fontSize:13}}>{errorGuardado}</div>
              )}
            <div className="cotz-totals" style={{marginTop:'14px',display:'flex',flexDirection:'column',gap:'6px',maxWidth:'360px'}}>
              <div style={{display:'flex',justifyContent:'space-between'}}><span>Subtotal bruto:</span><strong>{subtotalBruto.toFixed(2)}</strong></div>
              <div style={{display:'flex',justifyContent:'space-between'}}><span>Descuento:</span><strong>- {descuentoTotal.toFixed(2)}</strong></div>
              <div style={{display:'flex',justifyContent:'space-between'}}><span>Subtotal neto:</span><strong>{subtotal.toFixed(2)}</strong></div>
              {usarIVA && <div style={{display:'flex',justifyContent:'space-between'}}><span>IVA ({tasaIVA}%):</span><strong>{iva.toFixed(2)}</strong></div>}
              <div style={{display:'flex',justifyContent:'space-between'}}><span>Total:</span><strong>{total.toFixed(2)}</strong></div>
              <textarea placeholder="Notas (condiciones, entrega, etc.)" value={nota} onChange={e=>setNota(e.target.value)} style={{marginTop:'8px',minHeight:'70px'}} />
              <textarea placeholder="Condiciones al pie (garantías, tiempos, etc.)" value={condicionesPie} onChange={e=>setCondicionesPie(e.target.value)} style={{marginTop:'8px',minHeight:'60px'}} />
              <input placeholder="Firma (nombre del responsable)" value={firma} onChange={e=>setFirma(e.target.value)} />
              <div className="cotz-actions">
                <button type="button" disabled={guardando || imprimiendo} onClick={guardar} className="btn-modern primary" style={{flex:'1 1 auto',minWidth:130}}>
                  {guardando? 'Guardando...' : '💾 Guardar'}
                </button>
                <button disabled={imprimiendo} onClick={imprimir} className="btn-modern outline" style={{flex:'1 1 auto',minWidth:160}}>🖨️ Imprimir</button>
              </div>
              {mensajeGuardado && (
                <div style={{marginTop:6,fontSize:'11px',background:'#e8f5e9',padding:'6px 8px',border:'1px solid #c8e6c9',borderRadius:4,color:'#256029',display:'flex',gap:8,alignItems:'center'}}>
                  <div>Guardado como <strong>{mensajeGuardado.folio}</strong> (ID {mensajeGuardado.id_cotizacion})</div>
                  {mensajeGuardado.id_cotizacion && (
                    <button onClick={()=>imprimirGuardada(mensajeGuardado.id_cotizacion)} className="btn-modern outline" style={{fontSize:12}}>🖨️ Imprimir guardada</button>
                  )}
                </div>
              )}
            </div>
            </>
          )}
        </div>
      </div>
      {variantModal.open && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{background:'#fff',padding:'18px 22px',borderRadius:'8px',width:'360px',maxWidth:'90%',boxShadow:'0 4px 14px rgba(0,0,0,0.3)',fontFamily:'sans-serif'}}>
            <h3 style={{marginTop:0,fontSize:'18px'}}>Selecciona variante</h3>
            <p style={{fontSize:'12px',marginTop:0,color:'#555'}}>El código <strong>{variantModal.scannedCode}</strong> coincide con varias tallas/precios. Elige una:</p>
            <ul style={{listStyle:'none',padding:0,margin:'8px 0',maxHeight:'220px',overflowY:'auto'}}>
              {variantModal.options.map(p => (
                <li key={p.id} style={{marginBottom:'8px'}}>
                  <button onClick={()=>seleccionarVariante(p)} style={{width:'100%',textAlign:'left',background:'#1e88e5',color:'#fff',border:'none',padding:'10px 12px',borderRadius:'6px',cursor:'pointer',display:'flex',flexDirection:'column',gap:'3px'}}>
                    <span style={{fontWeight:'600',fontSize:'14px'}}>{p.concepto || 'Producto'} {p.talla ? `(${p.talla})` : ''}</span>
                    <span style={{fontSize:'12px',opacity:0.9}}>Precio: {Number(p.precio).toFixed(2)} | Stock: {p.cantidad ?? 0}</span>
                  </button>
                </li>
              ))}
            </ul>
            <div style={{display:'flex',justifyContent:'flex-end',gap:'10px',marginTop:'6px'}}>
              <button onClick={cerrarVariante} style={{background:'#ccc',border:'none',padding:'8px 14px',borderRadius:'4px',cursor:'pointer'}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const renderHistorial = () => (
    <div style={{marginTop:10}}>
      <h3 style={{margin:'0 0 10px'}}>Historial</h3>
      <HistorialFiltros />
      {histError && <div style={{color:'#b71c1c',fontSize:'12px',marginBottom:8}}>Error: {histError}</div>}
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
          <thead>
            <tr style={{background:'#eee'}}>
              <th style={{padding:6,border:'1px solid #ccc'}}>Folio</th>
              <th style={{padding:6,border:'1px solid #ccc'}}>Fecha</th>
              <th style={{padding:6,border:'1px solid #ccc'}}>Cliente</th>
              <th style={{padding:6,border:'1px solid #ccc'}}>Estado</th>
              <th style={{padding:6,border:'1px solid #ccc'}}>Subtotal</th>
              <th style={{padding:6,border:'1px solid #ccc'}}>IVA</th>
              <th style={{padding:6,border:'1px solid #ccc'}}>Total</th>
              <th style={{padding:6,border:'1px solid #ccc'}}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {histLista.map(c => (
              <tr key={c.id_cotizacion}>
                <td style={{padding:6,border:'1px solid #ddd'}}>{c.folio}</td>
                <td style={{padding:6,border:'1px solid #ddd'}}>{new Date(c.fecha).toLocaleString('es-MX')}</td>
                <td style={{padding:6,border:'1px solid #ddd'}}>{c.cliente || '-'}</td>
                <td style={{padding:6,border:'1px solid #ddd'}}>{c.estado || 'vigente'}</td>
                <td style={{padding:6,border:'1px solid #ddd'}}>{fmtMon(c.subtotal_neto)}</td>
                <td style={{padding:6,border:'1px solid #ddd'}}>{fmtMon(c.iva)}</td>
                <td style={{padding:6,border:'1px solid #ddd'}}>{fmtMon(c.total)}</td>
                <td style={{padding:6,border:'1px solid #ddd',display:'flex',gap:6,flexWrap:'wrap'}}>
                  <button onClick={()=>verHistorial(c.id_cotizacion)} style={{fontSize:'11px'}}>Ver</button>
                  <button onClick={()=>imprimirHistorial(c.id_cotizacion)} disabled={reimprimiendo} style={{fontSize:'11px'}}>{reimprimiendo? '...' : 'Imprimir'}</button>
                  <button onClick={async ()=>{
                    if(!window.confirm('¿Convertir esta cotización a venta?')) return;
                    try {
                      const resp = await fetchWithUserHeader(`/api/cotizaciones/${c.id_cotizacion}/convertir`, { method:'POST' });
                      if(!resp.ok){ const t = await resp.text(); throw new Error(t||'Error'); }
                      const d = await resp.json();
                      alert('Convertida a venta Folio #'+ (d.venta?.id_venta||''));
                      cargarHistorial();
                    } catch(e){ alert(e.message); }
                  }} style={{fontSize:'11px'}}>✓ Convertir</button>
                  <button onClick={async ()=>{
                    const dias = Number(prompt('¿Cuántos días renovar?', '7'))||7;
                    try{
                      const resp = await fetchWithUserHeader(`/api/cotizaciones/${c.id_cotizacion}/renovar`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ dias }) });
                      if(!resp.ok){ const t = await resp.text(); throw new Error(t||'Error'); }
                      await resp.json();
                      cargarHistorial();
                    } catch(e){ alert(e.message); }
                  }} style={{fontSize:'11px'}}>↻ Renovar</button>
                  <button onClick={async ()=>{
                    try{
                      setShareModal({ open:true, loading:true, wa:'', mail:'', url:'', folio: c.folio, cliente: c.cliente || '', error:'', idCot: c.id_cotizacion, pdfBlob: null });
                      
                      // Generar PDF
                      const pdfBlob = await generarPDFCotizacion(c.id_cotizacion);
                      
                      // Crear URL temporal del PDF
                      const pdfUrl = URL.createObjectURL(pdfBlob);
                      
                      // Preparar mensaje para WhatsApp
                      const mensaje = `Hola${c.cliente? ' '+c.cliente: ''}, te comparto la cotización ${c.folio} por ${fmtMon(c.total)}. PDF adjunto.`;
                      const wa = `https://api.whatsapp.com/send?text=${encodeURIComponent(mensaje)}`;
                      
                      setShareModal({ 
                        open:true, 
                        loading:false, 
                        wa, 
                        mail: '', 
                        url: pdfUrl, 
                        folio: c.folio, 
                        cliente: c.cliente || '', 
                        error:'', 
                        idCot: c.id_cotizacion,
                        pdfBlob
                      });
                    }catch(e){
                      setShareModal(prev => ({ ...prev, open:true, loading:false, error: e.message || 'Error generando PDF' }));
                    }
                  }} style={{fontSize:'11px'}}>📤 Compartir</button>
                  <button onClick={()=>eliminarHistorial(c.id_cotizacion, c.folio)} style={{fontSize:'11px',color:'#b71c1c'}}>Eliminar</button>
                </td>
              </tr>
            ))}
            {histLista.length===0 && !histCargando && (
              <tr><td colSpan={6} style={{padding:12,textAlign:'center',fontSize:'12px',opacity:.6}}>Sin cotizaciones guardadas</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {histDetalle && (
        <div style={{marginTop:18}}>
          <h4 style={{margin:'10px 0 6px'}}>Detalle {histDetalle.cotizacion.folio}</h4>
          <div style={{fontSize:'11px',marginBottom:6,opacity:.8}}>
            Fecha: {new Date(histDetalle.cotizacion.fecha).toLocaleString('es-MX')}
            {histDetalle.cotizacion.usar_iva ? ` | IVA ${Number(histDetalle.cotizacion.tasa_iva)||16}% aplicado` : ' | Sin IVA'}
            {histDetalle.cotizacion.cliente ? ` | Cliente: ${histDetalle.cotizacion.cliente}` : ''}
            {histDetalle.cotizacion.contacto ? ` | Contacto: ${histDetalle.cotizacion.contacto}` : ''}
            {histDetalle.cotizacion.vigencia_dias ? ` | Vigencia: ${histDetalle.cotizacion.vigencia_dias}d` : ''}
            {histDetalle.cotizacion.vigente_hasta ? ` | Vence: ${new Date(histDetalle.cotizacion.vigente_hasta+'T00:00:00').toLocaleDateString('es-MX')}`:''}
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
            <thead>
              <tr style={{background:'#f5f5f5'}}>
                <th style={{padding:4,border:'1px solid #ccc'}}>Producto</th>
                <th style={{padding:4,border:'1px solid #ccc'}}>Cant</th>
                <th style={{padding:4,border:'1px solid #ccc'}}>Precio</th>
                <th style={{padding:4,border:'1px solid #ccc'}}>Desc.U</th>
                <th style={{padding:4,border:'1px solid #ccc'}}>Importe</th>
              </tr>
            </thead>
            <tbody>
              {histDetalle.detalle.map(l => (
                <tr key={l.id_detalle}>
                  <td style={{padding:4,border:'1px solid #ddd'}}>{l.concepto}</td>
                  <td style={{padding:4,border:'1px solid #ddd'}}>{l.cantidad}</td>
                  <td style={{padding:4,border:'1px solid #ddd'}}>{fmtMon(l.precio_unitario)}</td>
                  <td style={{padding:4,border:'1px solid #ddd'}}>{Number(l.descuento_unitario)>0? fmtMon(l.descuento_unitario): '-'}</td>
                  <td style={{padding:4,border:'1px solid #ddd'}}>{fmtMon(l.importe)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{marginTop:10,display:'flex',flexDirection:'column',gap:2,maxWidth:260,fontSize:'11px'}}>
            <div style={{display:'flex',justifyContent:'space-between'}}><span>Subtotal bruto:</span><strong>{fmtMon(histDetalle.cotizacion.subtotal_bruto)}</strong></div>
            <div style={{display:'flex',justifyContent:'space-between'}}><span>Descuento:</span><strong>- {fmtMon(histDetalle.cotizacion.descuento_total)}</strong></div>
            <div style={{display:'flex',justifyContent:'space-between'}}><span>Subtotal neto:</span><strong>{fmtMon(histDetalle.cotizacion.subtotal_neto)}</strong></div>
            {histDetalle.cotizacion.usar_iva ? (
              <div style={{display:'flex',justifyContent:'space-between'}}><span>IVA ({Number(histDetalle.cotizacion.tasa_iva)||16}%):</span><strong>{fmtMon(histDetalle.cotizacion.iva)}</strong></div>
            ) : null}
            <div style={{display:'flex',justifyContent:'space-between'}}><span>Total:</span><strong>{fmtMon(histDetalle.cotizacion.total)}</strong></div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="cotizaciones-container cotz" style={{padding:'18px 24px'}}>
        <h2 style={{marginTop:0}}>Cotizaciones</h2>
        <div className="cotz-tabs" style={{marginTop:4,marginBottom:16}}>
          {['nueva','historial'].map(t => (
            <button key={t} onClick={()=>{ setTab(t); if(t==='historial' && histLista.length===0) cargarHistorial(); }}
              className={tab===t? 'active':''}>{t==='nueva'? '➕ Nueva':'📜 Historial'}</button>
          ))}
        </div>
        {tab==='nueva' ? renderNueva() : renderHistorial()}
      </div>
      {shareModal.open && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:10000}}>
          <div style={{background:'#fff',padding:'18px 22px',borderRadius:8,width:380,maxWidth:'92%',boxShadow:'0 4px 14px rgba(0,0,0,0.3)'}}>
            <h3 style={{marginTop:0,marginBottom:8,fontSize:18}}>Compartir {shareModal.folio || ''}</h3>
            {shareModal.loading ? (
              <div style={{fontSize:12,opacity:.8}}>Generando PDF...</div>
            ) : shareModal.error ? (
              <div style={{fontSize:12,color:'#b71c1c'}}>
                {shareModal.error}
                <div>
                  <button onClick={async ()=>{
                    if(!shareModal.idCot) return;
                    try{
                      setShareModal(prev=>({ ...prev, loading:true, error:'' }));
                      const pdfBlob = await generarPDFCotizacion(shareModal.idCot);
                      const pdfUrl = URL.createObjectURL(pdfBlob);
                      const mensaje = `Hola${shareModal.cliente? ' '+shareModal.cliente: ''}, te comparto la cotización ${shareModal.folio}. PDF adjunto.`;
                      const wa = `https://api.whatsapp.com/send?text=${encodeURIComponent(mensaje)}`;
                      setShareModal(prev=>({ ...prev, loading:false, wa, mail:'', url:pdfUrl, error:'', pdfBlob }));
                    }catch(e){ setShareModal(prev=>({ ...prev, loading:false, error: e.message||'Error generando PDF' })); }
                  }} className="btn-modern" style={{marginTop:8}}>Reintentar</button>
                </div>
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:10}}>
                <button onClick={async ()=> {
                  if(!shareModal.pdfBlob) return;
                  const file = new File([shareModal.pdfBlob], `cotizacion-${shareModal.folio}.pdf`, { type: 'application/pdf' });
                  
                  // Intentar usar Web Share API nativa (funciona en móviles)
                  if(navigator.share) {
                    try {
                      await navigator.share({
                        title: `Cotización ${shareModal.folio}`,
                        text: `Hola${shareModal.cliente? ' '+shareModal.cliente: ''}, te comparto la cotización ${shareModal.folio}.`,
                        files: [file]
                      });
                    } catch(e) {
                      if(e.name !== 'AbortError') {
                        // Si falla, descargar el archivo
                        const link = document.createElement('a');
                        link.href = shareModal.url;
                        link.download = `cotizacion-${shareModal.folio}.pdf`;
                        link.click();
                        alert('PDF descargado. Abre WhatsApp y adjunta el archivo desde tu galería/descargas.');
                      }
                    }
                  } else {
                    // Navegadores de escritorio: solo descargar
                    const link = document.createElement('a');
                    link.href = shareModal.url;
                    link.download = `cotizacion-${shareModal.folio}.pdf`;
                    link.click();
                    alert('PDF descargado. Abre WhatsApp Web y adjunta el archivo.');
                  }
                }} className="btn-modern primary" disabled={!shareModal.pdfBlob} style={{fontSize:14,padding:'12px'}}>
                  <span style={{fontSize:18}}>📱</span> Compartir PDF por WhatsApp
                </button>
                
                <button onClick={()=> {
                  if(shareModal.pdfBlob) {
                    const link = document.createElement('a');
                    link.href = shareModal.url;
                    link.download = `cotizacion-${shareModal.folio}.pdf`;
                    link.click();
                  }
                }} className="btn-modern outline" disabled={!shareModal.pdfBlob}>📄 Descargar PDF</button>
                
                <button onClick={()=> {
                  if(shareModal.url) window.open(shareModal.url, '_blank');
                }} className="btn-modern outline" disabled={!shareModal.url}>👁️ Visualizar PDF</button>
              </div>
            )}
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
              <button onClick={()=> {
                if(shareModal.url) URL.revokeObjectURL(shareModal.url);
                setShareModal({ open:false, loading:false, wa:'', mail:'', url:'', folio:'', cliente:'', error:'', idCot:null, pdfBlob:null });
              }} className="btn-cancelar">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
